import React from "react";
import { buildRpaFlowConfig } from "./buildRpaFlowConfig";

const STEP_COUNT = 6;

const styles = `
  .rpa-diag { background: #fff; border-radius: 8px; overflow: hidden; border: 1px solid #dbe7f7; font-family: Roboto, sans-serif; }
  .rpa-diag-head { background: #1d3f7d; padding: 14px 16px 12px; text-align: center; }
  .rpa-diag-head h4 { font-family: Exo, sans-serif; font-size: 15px; font-weight: 700; color: #fff; margin: 0 0 4px; }
  .rpa-diag-head p { margin: 0; font-size: 11px; color: rgba(255,255,255,0.9); line-height: 1.4; }
  .rpa-diag-hitl { background: #f4f7fb; padding: 10px 12px 6px; border-bottom: 1px solid #e2eaf4; }
  .rpa-diag-hitl-label { font-family: Exo, sans-serif; font-size: 9px; font-weight: 700; color: #c45d1e; text-transform: uppercase; text-align: center; margin: 0 0 8px; }
  .rpa-diag-hitl-row, .rpa-diag-body { display: grid; grid-template-columns: 108px repeat(${STEP_COUNT}, minmax(0,1fr)) 108px; gap: 4px; min-width: 0; }
  .rpa-diag-hitl-pill { grid-column: span 2; background: linear-gradient(180deg,#f5a84c 0%,#e8913a 100%); color: #fff; font-size: 9px; font-weight: 600; text-align: center; padding: 6px 4px; border-radius: 6px; }
  .rpa-diag-io { background: #f0f6fc; border: 1px solid #d4e3f5; border-radius: 6px; padding: 8px; align-self: stretch; }
  .rpa-diag-io h5 { font-size: 9px; font-weight: 700; color: #1d3f7d; text-transform: uppercase; text-align: center; margin: 0 0 6px; }
  .rpa-diag-io ul { margin: 0; padding-left: 14px; font-size: 8px; color: #3a4d62; line-height: 1.45; }
  .rpa-diag-step { background: linear-gradient(180deg,#fff 0%,#f7fbff 100%); border: 1px solid #d4e3f5; border-radius: 6px; padding: 6px 5px; min-height: 118px; display: flex; flex-direction: column; }
  .rpa-diag-step-head { display: flex; align-items: center; gap: 4px; margin-bottom: 4px; }
  .rpa-diag-step-num { width: 14px; height: 14px; border-radius: 50%; background: #1d3f7d; color: #fff; font-size: 8px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
  .rpa-diag-step-title { font-size: 8px; font-weight: 700; color: #1d3f7d; }
  .rpa-diag-step-icon { text-align: center; color: #27baa7; font-size: 16px; margin: 2px 0 4px; }
  .rpa-diag-step-tasks { margin: 0 0 auto; padding-left: 10px; font-size: 7px; color: #4a5d72; line-height: 1.35; flex: 1; }
  .rpa-diag-step-agent { margin-top: 4px; padding-top: 4px; border-top: 1px dashed #d4e3f5; text-align: center; }
  .rpa-diag-step-agent strong { display: block; font-size: 7px; color: #1d3f7d; }
  .rpa-diag-step-agent span { font-size: 6px; color: #27baa7; text-transform: uppercase; }
  .rpa-diag-systems { background: #1d3f7d; padding: 8px 12px; display: flex; flex-wrap: wrap; gap: 6px; justify-content: center; align-items: center; }
  .rpa-diag-systems span { font-size: 8px; font-weight: 600; color: #1d3f7d; background: #fff; border-radius: 4px; padding: 3px 8px; }
  .rpa-diag-systems p { margin: 0; font-size: 8px; color: rgba(255,255,255,0.85); }
  .rpa-diag-scroll { overflow-x: auto; }
`;

export default function RpaProcessFlow({ useCase }) {
  if (!useCase) return null;
  const config = buildRpaFlowConfig(useCase);

  return (
    <div className="rpa-diag" role="img" aria-label={config.title}>
      <style>{styles}</style>
      <header className="rpa-diag-head">
        <h4>{config.title}</h4>
        <p>{config.subtitle}</p>
      </header>
      <div className="rpa-diag-scroll">
        <div className="rpa-diag-hitl">
          <p className="rpa-diag-hitl-label">Human-in-the-loop checkpoints</p>
          <div className="rpa-diag-hitl-row">
            {config.checkpoints.map((cp) => (
              <div
                key={cp.label}
                className="rpa-diag-hitl-pill"
                style={{ gridColumn: `${cp.startCol} / span 2` }}
              >
                {cp.label}
              </div>
            ))}
          </div>
        </div>
        <div className="rpa-diag-body">
          <aside className="rpa-diag-io" aria-label="Inputs">
            <h5>Inputs</h5>
            <ul>
              {config.inputs.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </aside>
          {config.steps.map((step) => {
            const Icon = step.icon;
            return (
              <div key={step.id} className="rpa-diag-step">
                <div className="rpa-diag-step-head">
                  <span className="rpa-diag-step-num">{step.id}</span>
                  <span className="rpa-diag-step-title">{step.title}</span>
                </div>
                <div className="rpa-diag-step-icon">
                  <Icon />
                </div>
                <ul className="rpa-diag-step-tasks">
                  {step.tasks.map((task) => (
                    <li key={task}>{task}</li>
                  ))}
                </ul>
                <footer className="rpa-diag-step-agent">
                  <strong>{step.agent}</strong>
                  <span>{step.agentRole}</span>
                </footer>
              </div>
            );
          })}
          <aside className="rpa-diag-io" aria-label="Outputs">
            <h5>Outputs</h5>
            <ul>
              {config.outputs.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </aside>
        </div>
      </div>
      <footer className="rpa-diag-systems">
        <p>Systems:</p>
        {config.systems.map((system) => (
          <span key={system}>{system}</span>
        ))}
      </footer>
    </div>
  );
}

